import { Directive, ElementRef, forwardRef, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/** Propriedades do `vivest-input` (design system) usadas pela integração com formulários. */
type VivestInputElement = HTMLElement & { value: string; disabled: boolean };

/**
 * Liga o `<vivest-input>` do design system aos formulários do Angular (`formControlName`,
 * `formControl` e `ngModel`). O web component expõe `value`/`disabled` como propriedades e o
 * evento `input` do campo interno chega ao host, mas ele não implementa `ControlValueAccessor`.
 *
 * @example
 * <vivest-input label="Nome do Plano" formControlName="nome"></vivest-input>
 */
@Directive({
  selector: 'vivest-input[formControlName], vivest-input[formControl], vivest-input[ngModel]',
  standalone: true,
  host: {
    '(input)': 'aoDigitar()',
    '(focusout)': 'aoSair()',
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => VivestInputValueAccessorDirective),
      multi: true,
    },
  ],
})
export class VivestInputValueAccessorDirective implements ControlValueAccessor {
  private readonly elemento = inject<ElementRef<VivestInputElement>>(ElementRef).nativeElement;

  private onChange: (valor: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  writeValue(valor: string | number | null): void {
    this.elemento.value = valor === null || valor === undefined ? '' : String(valor);
  }

  registerOnChange(fn: (valor: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(desabilitado: boolean): void {
    this.elemento.disabled = desabilitado;
  }

  aoDigitar(): void {
    this.onChange(this.elemento.value);
  }

  aoSair(): void {
    this.onTouched();
  }
}
